<template>
  <ToolShell
    tool-name="Proposal follow-up generator"
    h1="Proposal follow-up generator for quiet clients."
    lede="A proposal follow-up is a short message that brings a quiet client back to a decision: a check-in around day 2, a reason to say yes around day 5, and a clean close around day 10. Enter the client and how long the proposal has been out to get that message for email and SMS. Free, no signup."
    :trust="['No signup', 'Email & SMS versions', 'Ready to copy']"
    form-title="Follow-up details"
    :upsell-points="[
      'You see the moment the client opens the proposal',
      'Follow-up emails and SMS go out on a schedule',
      'Approved proposals turn into invoices in one click',
    ]"
    free-label="The free generator"
    :free-bullets="[
      'One follow-up message, ready to copy and send yourself',
      'You remember when each follow-up goes out',
      'You can’t see whether the client even opened it',
    ]"
    upgrade-headline="This message works once. Workspace369 makes it a workflow."
    :connected-bullets="[
      'Proposal open tracking shows when the client reads it',
      'Follow-up emails and SMS run on a schedule with automations',
      'Approved proposals convert to invoices in one click',
      'Everything lands on the client record — status always visible',
    ]"
    slug="/tools/proposal-follow-up-generator"
    :meta-description="metaDescription"
    doc-brand="Proposal follow-up"
    :doc-subtitle="followUpSummary"
    doc-kind="EMAIL + SMS"
    stamp="Draft"
  >
    <template #form>
      <label class="ta-field">
        <span>Client name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.trim="form.client" type="text" placeholder="Maya" />
        </div>
      </label>

      <div class="ta-field-row">
        <label class="ta-field">
          <span>Proposal value ($, optional)</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5v13M11.5 4.5c-.6-1.2-1.9-1.8-3.5-1.8-1.9 0-3.2.9-3.2 2.4 0 3.4 7 1.7 7 5.1 0 1.6-1.5 2.5-3.8 2.5-1.8 0-3.2-.8-3.8-2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input v-model.number="form.value" type="number" min="0" step="100" placeholder="4800" />
          </div>
        </label>
        <label class="ta-field">
          <span>Days since sent</span>
          <div class="ta-input">
            <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input v-model.number="form.days" type="number" min="0" step="1" placeholder="5" />
          </div>
        </label>
      </div>

      <div class="ta-field">
        <span id="followup-tone-label">Tone</span>
        <ToolDropdown
          v-model="form.tone"
          :options="toneOptions"
          aria-label="Tone"
          labelledby="followup-tone-label"
        />
      </div>

      <button type="button" class="ta-download" @click="copyEmail">
        {{ copiedEmail ? "Copied!" : "Copy email" }}
        <svg v-if="!copiedEmail" viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        <svg v-else viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <button type="button" class="ta-download" @click="copySms">
        {{ copiedSms ? "Copied!" : "Copy SMS" }}
        <svg v-if="!copiedSms" viewBox="0 0 16 16" aria-hidden="true"><rect x="5" y="5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        <svg v-else viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <p class="ta-note">
        Proposals and email follow-up automations are on every plan from $29 (up to 3 active
        workflows); SMS steps start on Voyager ($74/month).
        <a
          :href="voyagerUrl"
          data-conversion-event="start_free_click"
          data-conversion-label="Proposal follow-up generator note Voyager trial"
          data-conversion-location="/tools/proposal-follow-up-generator"
          data-conversion-plan="voyager"
        >Start free on Voyager</a>.
      </p>
      <!-- The note ends on its Voyager trial link, so the full Voyager trial line sits directly under it (legal B1.1);
           the pricing link it used to end on sat between the two. -->
      <TrialDisclosure plan="voyager" />
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>To</span><strong>{{ form.client || "Client name" }}</strong></div>
        <div><span>Proposal value</span><strong>{{ valueLabel }}</strong></div>
        <div><span>Sent</span><strong>{{ sentLabel }}</strong></div>
        <div><span>Tone</span><strong>{{ toneLabel }}</strong></div>
      </div>

      <p class="ta-doc-body">{{ emailText }}</p>

      <p class="ta-doc-body">{{ smsText }}</p>

      <p class="ta-doc-note">The follow-up rhythm: check in at 2 days with value, follow up at 5 days with proof, and close cleanly at 10 days — then stop. Chasing longer costs more than the job.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Enter the proposal details</h2><p>The client’s name, the value if you want it in the message, and how long the proposal has been out.</p></div></div>
      <div><span>02</span><div><h2>Pick the tone</h2><p>Friendly check-in, value bump, or final nudge — the right one depends on the days since you sent it.</p></div></div>
      <div><span>03</span><div><h2>Copy and send</h2><p>An email version with a subject line and a short SMS version, both updated live as you type.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Proposal follow-up questions" :faq="faq" />

    <RelatedPages path="/tools/proposal-follow-up-generator" title="Proposal follow-up generator" />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";

const { addReferralToUrl } = useReferral();

const voyagerUrl = computed(() => addReferralToUrl("https://app.workspace369.com/signup?plan=voyager"));

const form = reactive({ client: "", value: null, days: 5, tone: "value" });

const copiedEmail = ref(false);
const copiedSms = ref(false);

const toneOptions = [
  { value: "friendly", label: "Friendly check-in", hint: "around day 2" },
  { value: "value", label: "Value bump", hint: "around day 5" },
  { value: "final", label: "Final nudge", hint: "day 10+" },
];

const client = computed(() => form.client || "there");
const toneLabel = computed(
  () => toneOptions.find((option) => option.value === form.tone)?.label || "",
);

const money = (value) =>
  `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const valueLabel = computed(() => (Number(form.value) > 0 ? money(form.value) : "Not set"));
const valuePhrase = computed(() => (Number(form.value) > 0 ? ` for ${money(form.value)}` : ""));

const sentDays = computed(() => {
  const value = form.days;
  if (value == null || (typeof value === "string" && value.trim() === "")) return null;
  const days = Number(value);
  return Number.isInteger(days) && days >= 0 ? days : null;
});

const sentLabel = computed(() => {
  const days = sentDays.value;
  if (days === 0) return "Today";
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} ago`;
  return "Not set";
});
const sentPhrase = computed(() => {
  const days = sentDays.value;
  if (days > 0) return ` I sent ${days} day${days === 1 ? "" : "s"} ago`;
  return "";
});

const followUpSummary = computed(() => `${toneLabel.value} · sent ${sentLabel.value.toLowerCase()}`);

const link = "[proposal link]";

const subject = computed(() => {
  if (form.tone === "friendly") return "Any questions on the proposal?";
  if (form.tone === "value") return "One more thing before you decide";
  return "Should I close the file on this one?";
});

const emailBody = computed(() => {
  if (form.tone === "friendly") {
    return `Hi ${client.value} — just making sure the proposal${valuePhrase.value} landed. Short version: one price, everything we talked about is included, and I can start as soon as next week. If anything is unclear, I’m happy to walk you through it on a quick call. If it looks good, you can approve it right from the link: ${link}`;
  }
  if (form.tone === "value") {
    return `Hi ${client.value} — quick follow-up on the proposal${valuePhrase.value}${sentPhrase.value}. One more thing worth adding: [one line of proof — a recent result, a review, or your guarantee]. The price holds until [date], and I’m glad to walk you through it on a 10-minute call. The link is here if you want another look: ${link}`;
  }
  return `Hi ${client.value} — I haven’t heard back on the proposal${valuePhrase.value}, so I’ll assume the timing isn’t right and close the file on my side. No hard feelings at all. If the project comes back to life, just reply and I’ll re-quote within a day. Thanks for considering me.`;
});

const smsBody = computed(() => {
  if (form.tone === "friendly") {
    return `Hi ${client.value} — did the proposal${valuePhrase.value} come through? Happy to answer anything. If it looks good, you can approve it from the link: ${link}`;
  }
  if (form.tone === "value") {
    return `Hi ${client.value} — still weighing the proposal${valuePhrase.value}? One more reason: [your best proof]. The price holds until [date]. Questions welcome: ${link}`;
  }
  return `Hi ${client.value} — I’ll close the file on the proposal${valuePhrase.value} for now. If the project comes back later, text me and I’ll re-send it. Thanks either way!`;
});

const emailText = computed(() => `Email — subject: ${subject.value}\n\n${emailBody.value}`);
const smsText = computed(() => `SMS version (${smsBody.value.length} characters):\n\n${smsBody.value}`);

const copyEmail = async () => {
  try {
    await navigator.clipboard.writeText(`Subject: ${subject.value}\n\n${emailBody.value}`);
    copiedEmail.value = true;
    setTimeout(() => (copiedEmail.value = false), 1600);
  } catch { /* clipboard unavailable */ }
};

const copySms = async () => {
  try {
    await navigator.clipboard.writeText(smsBody.value);
    copiedSms.value = true;
    setTimeout(() => (copiedSms.value = false), 1600);
  } catch { /* clipboard unavailable */ }
};

const guideSections = [
  {
    title: "When to send each follow-up",
    paragraphs: [
      "Three messages cover most quiet proposals. Each one has a different job, so the tone changes as the days pass.",
    ],
    bullets: [
      "Around day 2, a friendly check-in: make sure the proposal arrived and offer to answer questions.",
      "Around day 5, a value bump: add one new reason to say yes and a date the price holds until.",
      "Around day 10, a final nudge: close the file politely and leave the door open.",
      "After that, stop. A client who wants to go ahead can still reply to any of the three.",
    ],
  },
  {
    title: "What each message says",
    paragraphs: [
      "The friendly check-in confirms the proposal landed, sums it up as one price with everything you discussed included, offers a quick call, and points to the link where the client can approve it.",
      "The value bump adds one line of proof, such as a recent result, a review or your guarantee, says how long the price holds, and offers a 10-minute call.",
      "The final nudge says you will assume the timing is not right and close the file, with no hard feelings, and offers to re-quote within a day if the project comes back.",
    ],
  },
  {
    title: "Fill in the brackets before you send",
    paragraphs: [
      "The messages leave a few placeholders in square brackets, because only you know what goes there. Replace every one before you copy the message out.",
    ],
    bullets: [
      "[proposal link]: the link the client opens to read and approve the proposal.",
      "[one line of proof], or [your best proof] in the text version: one specific result, review or guarantee, not a list.",
      "[date]: the day the price stops holding. Pick one you will keep to.",
      "Client name: if you leave it blank, the message opens with Hi there.",
      "Proposal value: optional. When you add it, the message names the amount.",
    ],
  },
  {
    title: "Email or text",
    paragraphs: [
      "Use email when the client has been emailing you, or when the decision involves other people who will read the thread. Use a text when the client has been texting you and the message is short. The SMS version shows its character count so you can keep it brief. Only text clients who have given you their number for this kind of message.",
    ],
    links: [
      { label: "Client follow-up email examples", path: "/learn/client-follow-up-email" },
      { label: "How to write a proposal", path: "/learn/how-to-write-a-proposal" },
    ],
  },
  {
    title: "Following up in Workspace369",
    paragraphs: [
      "On every plan, Workspace369 shows when a client opens and engages with a proposal, collects a deposit when they accept, and turns an approved proposal into an invoice. Email follow-up workflows also run on every plan, with up to 3 active workflows on Cadet; text steps start on Voyager.",
    ],
    links: [
      { label: "Proposals and quotes", path: "/features/quoting" },
      { label: "Workflow automations", path: "/features/automation" },
    ],
  },
];

const faq = [
  {
    q: "How long should I wait to follow up on a proposal?",
    a: "About two days for the first check-in. That is long enough for the client to have read it and short enough that the conversation is still fresh. Follow up again around day 5 and close it around day 10.",
  },
  {
    q: "How many times should I follow up?",
    a: "Three times is usually enough: a check-in, a value reminder and a final message that closes the file. More than that tends to cost more time than the job is worth, and a client who is ready can reply to any of them.",
  },
  {
    q: "What should a proposal follow-up email say?",
    a: "Keep it short. Confirm the proposal arrived, give one new reason to say yes or answer a likely question, include the link to approve it, and make the next step easy, such as a short call.",
  },
  {
    q: "Should I offer a discount in a follow-up?",
    a: "Usually not as the first move. A discount teaches the client to wait. Restate the value, add proof, or offer a smaller first phase instead. If the price is the real blocker, ask before you change it.",
  },
];

const metaTitle = "Proposal Follow-Up Generator | Workspace369";
const metaDescription =
  "Free proposal follow-up generator: ready-to-send check-in, value, and final-nudge follow-ups for email and SMS, timed to when you sent the proposal.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
}));
</script>
